import React, { useState, useCallback, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import LinearProgress from '@material-ui/core/LinearProgress'
import { useSnackbar } from 'notistack'
import { useFormContext } from 'react-hook-form'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import CheckIcon from 'components/material-symbol/icons/Check'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import EditEventPagination from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-pagination'
import FormSectionContainer from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-section-container'
import EditBookingTypeSectionNav, {
  EDIT_BOOKING_TYPE_SECTIONS,
  getActiveSectionIndex,
} from './edit-booking-type-section-nav'
import BookingTypeDetails from 'pages/pga-coach/bookings/booking-types/components/booking-type-details'
import VirtualCoachingSettings from 'pages/pga-coach/bookings/booking-types/components/virtual-coaching-settings'
import SingleOrLessonPack from 'pages/pga-coach/bookings/booking-types/components/single-or-lesson-packs'
import BookingTypePaymentOptions from 'pages/pga-coach/bookings/booking-types/components/booking-type-payment-options'
import BookingTypeAdditionalOptions from 'pages/pga-coach/bookings/booking-types/components/booking-type-additional-options'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'
import { VIRTUAL_COACHING_TEMPLATE } from 'pages/pga-coach/bookings/booking-types/offering-kind'
import { RequiredFieldsNote } from 'pages/pga-coach/bookings/booking-types/components/required-field'

const EditBookingTypeLayout = ({
  history,
  bookingType,
  isCreateFlow = false,
  isLessonPacks,
  setIsLessonPacks,
  isVirtualCoaching = false,
  setIsVirtualCoaching,
  showVirtualCoaching = false,
  isSubmitting,
  isFormValid = true,
  isFormDirty = false,
  coachFacilities,
  coachAvailabilities,
  coachingCenters,
  durationInMinutesOptions,
  categories,
  handleCategoryChange,
  startTimeIncrementOptions,
  paymentAccounts,
  singleLessonPrice,
  errors,
  control,
  register,
  watch,
  status,
  setStatus,
  giftingEnabled,
  setGiftingEnabled,
  trigger,
  onCancel,
  validationErrorHandlerRef,
}) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const { setValue } = useFormContext()
  const [isApplyingTemplate, setIsApplyingTemplate] = useState(false)

  // The description editor is uncontrolled, so it is unmounted while the
  // template values are written and then remounted to pick them up.
  useEffect(() => {
    if (!isApplyingTemplate) return
    const options = { shouldDirty: true, shouldValidate: false }
    setValue('title', VIRTUAL_COACHING_TEMPLATE.title, options)
    setValue('description', VIRTUAL_COACHING_TEMPLATE.description, options)
    setValue(
      'turnaroundHours',
      VIRTUAL_COACHING_TEMPLATE.turnaroundHours,
      options,
    )
    setIsApplyingTemplate(false)
  }, [isApplyingTemplate, setValue])
  const [activeSection, setActiveSection] = useState(
    EDIT_BOOKING_TYPE_SECTIONS[0].id,
  )
  const activeStepIndex = getActiveSectionIndex(activeSection)
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === EDIT_BOOKING_TYPE_SECTIONS.length - 1
  const currentSection = EDIT_BOOKING_TYPE_SECTIONS[activeStepIndex]

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (!validationErrorHandlerRef) return undefined

    validationErrorHandlerRef.current = formErrors => {
      const errorFields = Object.keys(formErrors)
      const sectionWithError = EDIT_BOOKING_TYPE_SECTIONS.find(section =>
        (section.fields || []).some(field => errorFields.includes(field)),
      )

      if (sectionWithError) {
        setActiveSection(sectionWithError.id)
        scrollToFormTop()
      }
    }

    return () => {
      validationErrorHandlerRef.current = () => {}
    }
  }, [scrollToFormTop, validationErrorHandlerRef])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveSection(sectionId)
      scrollToFormTop()
    },
    [scrollToFormTop],
  )

  const getStepFields = useCallback(
    section => {
      if (isVirtualCoaching && section.id === 'edit-section-details') {
        return [
          'title',
          'description',
          'location',
          'turnaroundHours',
          'purchaseLimit',
        ]
      }
      if (section.id === 'edit-section-payment' && isLessonPacks) {
        return ['paymentAccountId', 'lessonPacks']
      }
      if (section.id === 'edit-section-payment' && !isLessonPacks) {
        return ['price', 'paymentAccountId']
      }
      return section.fields || []
    },
    [isLessonPacks, isVirtualCoaching],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveSection(EDIT_BOOKING_TYPE_SECTIONS[activeStepIndex - 1].id)
      scrollToFormTop()
    }
  }, [activeStepIndex, isFirstStep, scrollToFormTop])

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = getStepFields(currentSection)
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid) {
      setActiveSection(EDIT_BOOKING_TYPE_SECTIONS[activeStepIndex + 1].id)
      scrollToFormTop()
      return
    }

    const errorMessage = getFirstErrorMessage(errors || {})

    enqueueSnackbar(errorMessage || 'Fix the highlighted fields to continue.', {
      variant: 'error',
    })
  }, [
    activeStepIndex,
    currentSection,
    enqueueSnackbar,
    errors,
    getStepFields,
    isLastStep,
    scrollToFormTop,
    trigger,
  ])

  let createLabel = 'Create Booking Type'
  if (isLessonPacks) createLabel = 'Create Lesson Pack'
  if (isVirtualCoaching) createLabel = 'Create Virtual Coaching'
  const submitLabel = isCreateFlow ? createLabel : 'Save'

  const canSubmit = isCreateFlow ? isLastStep : isFormDirty

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    history.goBack()
  }

  return (
    <Box>
      <Box className={classes.actionBar}>
        <Button
          type="button"
          className={classes.cancelAction}
          startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.saveAction}
          startIcon={<CheckIcon fontSize="small" />}
          data-cy="submit"
          disabled={isSubmitting || !canSubmit}
        >
          {submitLabel}
        </Button>
      </Box>

      {isSubmitting ? (
        <LinearProgress color="primary" className={classes.loadingStyles} />
      ) : null}

      <Container maxWidth="lg">
        <Box pt={4} pb={3}>
          <Box className={classes.layoutGrid}>
            <Box className={classes.navColumn}>
              <EditBookingTypeSectionNav
                activeSection={activeSection}
                onSectionClick={handleSectionClick}
              />
            </Box>

            <Box className={classes.formColumn}>
              <Box hidden={activeSection !== 'edit-section-details'}>
                <FormSectionContainer
                  id={EDIT_BOOKING_TYPE_SECTIONS[0].id}
                  title={EDIT_BOOKING_TYPE_SECTIONS[0].pageTitle}
                >
                  <RequiredFieldsNote />
                  <SingleOrLessonPack
                    isEditFlow
                    isLessonPacks={isLessonPacks}
                    setIsLessonPacks={setIsLessonPacks}
                    isVirtualCoaching={isVirtualCoaching}
                    setIsVirtualCoaching={setIsVirtualCoaching}
                    showVirtualCoaching={showVirtualCoaching}
                    readOnly={!isCreateFlow && isVirtualCoaching}
                    paymentAccounts={paymentAccounts}
                  />
                  {isApplyingTemplate ? null : (
                    <BookingTypeDetails
                      isEditFlow
                      isVirtualCoaching={isVirtualCoaching}
                      coachFacilities={coachFacilities}
                      coachAvailabilities={coachAvailabilities}
                      coachingCenters={coachingCenters}
                      durationInMinutesOptions={durationInMinutesOptions}
                      categories={categories}
                      handleCategoryChange={handleCategoryChange}
                      startTimeIncrementOptions={startTimeIncrementOptions}
                    />
                  )}
                  {isVirtualCoaching ? (
                    <VirtualCoachingSettings
                      isCreateFlow={isCreateFlow}
                      onApplyTemplate={() => setIsApplyingTemplate(true)}
                    />
                  ) : null}
                </FormSectionContainer>
              </Box>
              <Box hidden={activeSection !== 'edit-section-payment'}>
                <FormSectionContainer
                  id={EDIT_BOOKING_TYPE_SECTIONS[1].id}
                  title={EDIT_BOOKING_TYPE_SECTIONS[1].pageTitle}
                >
                  {isLessonPacks || isVirtualCoaching ? (
                    <RequiredFieldsNote />
                  ) : null}
                  <BookingTypePaymentOptions
                    isEditFlow
                    errors={errors}
                    control={control}
                    register={register}
                    watch={watch}
                    isLessonPacks={isLessonPacks}
                    isVirtualCoaching={isVirtualCoaching}
                    paymentAccounts={paymentAccounts}
                    singleLessonPrice={singleLessonPrice}
                  />
                </FormSectionContainer>
              </Box>
              <Box hidden={activeSection !== 'edit-section-options'}>
                <FormSectionContainer
                  id={EDIT_BOOKING_TYPE_SECTIONS[2].id}
                  title={EDIT_BOOKING_TYPE_SECTIONS[2].pageTitle}
                >
                  <BookingTypeAdditionalOptions
                    isEditFlow
                    status={status}
                    setStatus={setStatus}
                    bookingType={bookingType}
                    isLessonPacks={isLessonPacks}
                    giftingEnabled={giftingEnabled}
                    setGiftingEnabled={setGiftingEnabled}
                  />
                </FormSectionContainer>
              </Box>
              <EditEventPagination
                isFirstStep={isFirstStep}
                isLastStep={isLastStep}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default EditBookingTypeLayout
